import { randomUuid } from '../api/active/randomUuid';
import type { ActiveTransport } from '../api/active/transport';

/**
 * Phase 1 administration client for the frozen `/users`, `/departments` and
 * `/roles` operations. The Active53 generated client only covers the operations
 * released to the quotation workbench, so these are typed here against the very
 * same frozen schemas (`AdminUser`, `UserPage`, `Department`, `DepartmentList`,
 * `Role`, `RoleList`). Field names and nullability must stay aligned with
 * `server-v1/openapi/contract.yaml`; a shape change is a contract change.
 */
export type AdministratedUser = {
  userId: string;
  wecomUserId: string | null;
  companyId: string;
  displayName: string;
  wecomDepartments: Array<{ id: string; path: string }>;
  status: 'active' | 'disabled' | 'pending_authorization';
  managementIdentity?: 'superadmin' | 'admin' | 'member' | null;
  roleIds: string[];
  departmentIds: string[];
  primaryDepartmentId: string | null;
  permissionVersion: string;
  rowVersion: string;
  createdAt: string;
  updatedAt: string;
};

export type AdministratedUserPage = {
  items: AdministratedUser[];
  wecomDepartmentOptions: Array<{ id: string; path: string }>;
  meta: { page: number; pageSize: number; total: number; totalPages: number };
};

export type WeComMemberSyncResult = {
  created: number;
  updated: number;
  unchanged: number;
  total: number;
};

export type AdministratedDepartment = {
  id: string;
  parentId?: string | null;
  code: string;
  name: string;
  active: boolean;
  defaultRoleId?: string | null;
  rowVersion?: string;
  assignmentRoleId?: string | null;
  assignmentRoleActive?: boolean;
};

export type AdministratedRole = {
  id: string;
  key: string;
  name: string;
  systemRoleType: 'superadmin' | null;
  migrationWarnings?: string[];
  active: boolean;
  permissionCodes: string[];
  permissionDenials?: string[];
  stateActionDenials?: string[];
  dataScopes: Array<{
    resourceType: string;
    scopeType: string;
    departmentIds?: string[];
  }>;
  fieldPolicies: Array<Record<string, unknown>>;
  stateActions: string[];
  permissionVersion: string;
  rowVersion: string;
};

export type UserAuthorizationWrite = {
  displayName?: string;
  status: 'active' | 'disabled' | 'pending_authorization';
  roleIds: string[];
  departmentIds: string[];
  primaryDepartmentId: string;
  revokeAllSessions: boolean;
};

export type RolePolicyWrite = {
  authorizationPolicyVersion?: '2';
  key: string;
  name: string;
  active: boolean;
  permissionCodes: string[];
  permissionDenials?: string[];
  stateActionDenials?: string[];
  dataScopes: Array<{
    resourceType: string;
    scopeType: string;
    departmentIds?: string[];
  }>;
  fieldPolicies: Array<Record<string, unknown>>;
  stateActions: string[];
};

export class PermissionAdminError extends Error {
  constructor(
    readonly status: number | null,
    readonly detail: string,
    readonly code?: string,
  ) {
    super(detail);
  }
}

async function parseError(result: Response): Promise<PermissionAdminError> {
  const problem = (await result.json().catch(() => ({}))) as {
    code?: string;
    detail?: string;
    title?: string;
  };
  return new PermissionAdminError(
    result.status,
    problem.detail ??
      problem.title ??
      `后端请求失败（HTTP ${result.status}）。`,
    problem.code,
  );
}

async function read<T>(result: Response): Promise<T> {
  if (!result.ok) throw await parseError(result);
  return (await result.json()) as T;
}

function mutationHeaders(
  csrfToken: string | null,
  ifMatch: string,
): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    'Idempotency-Key': randomUuid(),
    ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
    'If-Match': `"${ifMatch}"`,
  };
}

export function createPermissionAdminApi(
  transport: ActiveTransport,
  csrfToken: string | null,
) {
  return {
    syncWeComMembers: async (signal?: AbortSignal) => {
      const result = await transport.requestResponse('/users:sync-wecom', {
        method: 'POST',
        headers: {
          'Idempotency-Key': randomUuid(),
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
        },
        signal,
      });
      return read<WeComMemberSyncResult>(result);
    },
    listUsers: (
      page: number,
      pageSize: number,
      search: string,
      signal?: AbortSignal,
      wecomDepartmentId?: string,
      roleId?: string,
      filters?: {
        departmentId?: string;
        managementIdentity?: 'superadmin' | 'admin' | 'member';
        status?: 'active' | 'disabled' | 'pending_authorization';
      },
    ) =>
      transport
        .requestResponse(
          `/users?page=${page}&pageSize=${pageSize}&search=${encodeURIComponent(search)}${wecomDepartmentId ? `&wecomDepartmentId=${encodeURIComponent(wecomDepartmentId)}` : ''}${roleId ? `&roleId=${encodeURIComponent(roleId)}` : ''}${filters?.departmentId ? `&departmentId=${encodeURIComponent(filters.departmentId)}` : ''}${filters?.managementIdentity ? `&managementIdentity=${encodeURIComponent(filters.managementIdentity)}` : ''}${filters?.status ? `&status=${encodeURIComponent(filters.status)}` : ''}`,
          { cache: 'no-store', signal },
        )
        .then(read<AdministratedUserPage>),
    listDepartments: (signal?: AbortSignal) =>
      transport
        .requestResponse('/departments', { cache: 'no-store', signal })
        .then(read<{ items: AdministratedDepartment[] }>),
    listRoles: (signal?: AbortSignal) =>
      transport
        .requestResponse('/roles', { cache: 'no-store', signal })
        .then(read<{ items: AdministratedRole[] }>),
    getUser: async (userId: string, signal?: AbortSignal) => {
      const result = await transport.requestResponse(
        `/users/${encodeURIComponent(userId)}`,
        { cache: 'no-store', signal },
      );
      if (!result.ok) throw await parseError(result);
      return {
        user: (await result.json()) as AdministratedUser,
        etag: result.headers.get('ETag') ?? '',
      };
    },
    updateUserAuthorization: async (
      userId: string,
      rowVersion: string,
      body: UserAuthorizationWrite,
      signal?: AbortSignal,
    ) => {
      const result = await transport.requestResponse(
        `/users/${encodeURIComponent(userId)}`,
        {
          method: 'PATCH',
          headers: mutationHeaders(csrfToken, rowVersion),
          body: JSON.stringify(body),
          signal,
        },
      );
      if (!result.ok) throw await parseError(result);
      return {
        user: (await result.json()) as AdministratedUser,
        etag: result.headers.get('ETag') ?? '',
      };
    },
    updateRolePolicy: async (
      roleId: string,
      rowVersion: string,
      body: RolePolicyWrite,
      signal?: AbortSignal,
    ) => {
      const result = await transport.requestResponse(
        `/roles/${encodeURIComponent(roleId)}`,
        {
          method: 'PATCH',
          headers: mutationHeaders(csrfToken, rowVersion),
          body: JSON.stringify(body),
          signal,
        },
      );
      if (!result.ok) throw await parseError(result);
      return {
        role: (await result.json()) as AdministratedRole,
        etag: result.headers.get('ETag') ?? '',
      };
    },
  };
}

export type PermissionAdminApi = ReturnType<typeof createPermissionAdminApi>;
